// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : XMenu
// =============================================================================
// ---Third-party imports
import React, {useRef} from 'react'
import {isFragment, isElement} from 'react-is'
import {Menu, Icon, Dropdown, Button, Input, Popup, Header, Modal, Label} from 'semantic-ui-react'
import clsx from 'clsx'

import {isValid, parse} from 'date-fns'

// ---Framework imports
import {
  isNil,
  isObject,
  isBoolean,
  isArray,
  isString,
  isFunction,
  isNumber,
  callIfFunc,
  dict,
  keys,
  has,
  mapItems,
  size as ldash_size,
} from 'ldash'
import log from 'uix/logging'
import {
  useDebounce,
  useEffect,
  useState,
  useSelector,
  useIsOffline,
  useActions,
  useCallback,
  usePersistentWithQueryOverride,
  useFullscreenContext,
  useResettableState,
  usePartContext,
} from 'uix/hooks'
import {VIcon, Icons, vicon} from 'uix/graphics'
import {Sel, Actions} from 'uix/registries'
import {checkAcl} from 'uix/auth/acl'

// ---Local imports
import styles from './menu.scss'
import {AutoPortal} from './portals'
import {applyProps} from './utils'
import {KeyboardShortCut} from './keyboardShortcuts'
import {DatePicker} from './datepicker'
import {ResizableDialog} from './dialog'
import {Link} from './links'
import {XPopup} from './popup'
import {SearchableJSONTree} from 'uix/chunks'
import {DebugFeature} from './debug'

// ---Globals

const PPrintButton = ({
  data,
  defaultQuery = '',
  expand,
  icon = 'bug',
  content,
  title,
  open,
  keepOpen,
  header,
  position,
  compact = false,
  popupStyle,
  as = 'dialog', // else popup
}) => {
  let [expanded, setExpanded] = useState(false)
  let [query, setQuery] = useState(defaultQuery)
  if (expand) {
    expanded = expand
  }
  let shouldExpandNode = expanded ? () => true : undefined

  let treeStyle = {}
  if (compact) {
    treeStyle = {
      padding: '0.2em',
      margin: '0.5em',
      lineHeight: '0.8em',
      fontSize: '0.7em',
    }
  }
  let theme = {
    tree: ({style, className}) => {
      style = {...style, ...treeStyle}
      return {style, className}
    },
    nestedNodeLabel: ({style}, keyPath, nodeType, expanded) => ({style}),
  }

  let jsontree = (
    <SearchableJSONTree
      hideRoot
      data={data}
      query={query}
      shouldExpandNode={shouldExpandNode}
      theme={theme}
    />
  )

  let itemProps = {icon, content, title}
  if (as == 'dialog') {
    return (
      <DialogItem {...itemProps} dlgHeader={header || title}>
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
          <XMenu attached size="small" style={{width: '99%'}}>
            <XMenu.Toggle value={expanded} set={setExpanded} content="Expand" />
            <XMenu.Input icon="filter" debounce={300} value={query} onValueChange={setQuery} />
          </XMenu>
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              lineHeight: '1rem',
              fontSize: '0.9rem',
              backgroundColor: 'rgb(0, 43, 54)',
            }}
          >
            {jsontree}
          </div>
        </div>
      </DialogItem>
    )
  } else if (as == 'popup') {
    return (
      <XPopupItem
        {...itemProps}
        open={open}
        keepOpen={keepOpen}
        basic
        position={position}
        popupStyle={popupStyle}
      >
        <Button
          style={{
            position: 'absolute',
            right: '0px',
            zIndex: 100,
            marginTop: '-5px',
          }}
          secondary
          compact
          size="mini"
          icon="expand"
          active={expanded}
          onClick={() => setExpanded(!expanded)}
        />
        {jsontree}
      </XPopupItem>
    )
  } else {
    log.error('invalid mode: %s', as)
    return null
  }
}

const DebugButton = ({
  portalTarget,
  open,
  publicVisible = false, // default: only visible for debug group
  onAction, // add another button that runs this onClick
  ...props
}) => {
  let button = (
    <PPrintButton
      as="popup"
      keepOpen
      open={open}
      compact
      {...props}
      popupStyle={{minWidth: '70px', maxHeight: '70vh', overflow: 'auto'}}
    />
  )
  if (onAction) {
    button = (
      <>
        {button}
        <XItem
          icon={['bug', {name: 'play', color: 'red'}]}
          onClick={onAction}
          title="Run debug action"
        />
      </>
    )
  }
  if (portalTarget) {
    button = <AutoPortal target={portalTarget}>{button}</AutoPortal>
  }

  if (!publicVisible) {
    button = <DebugFeature>{button}</DebugFeature>
  }

  return button
}

let XMenu = ({
  size = null,
  padded = false,
  className = null,
  id,
  ref,
  children,
  borderless,
  ...props
}) => {
  /* menu that supports semantics ui style*/
  //~ children = applyProps(children, {mode})

  return (
    <Menu
      ref={ref}
      className={clsx(padded && styles.padded, className, borderless && styles.borderless)}
      size={size}
      {...props}
    >
      {children}
    </Menu>
  )
}

let buildConfirmation = ({
  confirmSpec,
  confirmButton = 'OK',
  confirmationOpen,
  setConfirmationOpen,
  onConfirmed,
  i18n,
}) => {
  let onConfirmClicked = () => {
    onConfirmed()
    setConfirmationOpen(false)
  }
  //~ log.x(confirm)
  let contentConfirm = isBoolean(confirmSpec) ? undefined : confirmSpec
  let modal_options = contentConfirm?.modalOptions || {}
  let disabled = false

  let buildContent = content => {
    if (isFunction(content)) {
      return content()
    }
    return content
  }
  let header = null
  let onConfirmationOpen = null
  let onConfirmationClose = null
  let cancelButton = i18n.get('abort')
  if (isElement(contentConfirm) || isFragment(contentConfirm)) {
    // raw react
    contentConfirm = <div className="content">{buildContent(contentConfirm)}</div>
  } else if (isObject(contentConfirm)) {
    // options: confirmButton, onOpen, onClose
    let {content, buttonDisabled, header: h, size} = contentConfirm
    header = h
    if (has(contentConfirm, 'confirmButton')) {
      // pass through null for explicit disable
      confirmButton = contentConfirm.confirmButton
    }
    if (contentConfirm.cancelButton) {
      cancelButton = contentConfirm.cancelButton
    }
    modal_options = {...modal_options, size}
    disabled = !!buttonDisabled
    contentConfirm = <div className="content">{buildContent(content)}</div>
    // handle onOpen + onClose
    onConfirmationOpen = contentConfirm.onOpen
    onConfirmationClose = contentConfirm.onClose
  }

  useEffect(() => {
    if (!isNil(confirmationOpen)) {
      if (onConfirmationOpen && confirmationOpen) onConfirmationOpen()
      if (onConfirmationClose && !confirmationOpen) onConfirmationClose()
    }
  }, [confirmationOpen])

  return (
    <Modal size="small" open={confirmationOpen} {...modal_options}>
      <KeyboardShortCut name="Escape" onTrigger={e => setConfirmationOpen(false)} />
      <KeyboardShortCut name="Enter" onTrigger={e => (disabled ? null : onConfirmClicked())} />
      {modal_options.header && <Modal.Header content={modal_options.header} />}
      {header && <Modal.Header>{header}</Modal.Header>}
      <Modal.Content content={contentConfirm} />
      <Modal.Actions>
        <Button secondary onClick={() => setConfirmationOpen(false)}>
          {cancelButton}
        </Button>
        {confirmButton && (
          <Button primary disabled={disabled} content={confirmButton} onClick={onConfirmClicked} />
        )}
      </Modal.Actions>
    </Modal>
  )
}

let XItem = ({
  content,
  icon, // give a list of icons for corner icons
  className,
  disabled,
  active,
  children,
  confirm, // show confirm button, true/false or renderable content or full options
  cornerContent, // show small additional stuff positioned above
  shortcut, // keyboard shortcut (global)
  acl, // group restrictions formatted like 'g:admin,g:vlm'
  prominent = false,
  ...props
}) => {
  let {i18n, user, dname} = usePartContext()

  let isOffline = useIsOffline()
  disabled = disabled || isOffline

  let confirmation = null
  let [confirmationOpen, setConfirmationOpen] = useState(false)

  if (confirm) {
    let {onClick, ...otherProps} = props
    props = {...props, onClick: () => setConfirmationOpen(true)}
    confirmation = React.createElement(buildConfirmation, {
      confirmSpec: confirm,
      confirmationOpen,
      setConfirmationOpen,
      onConfirmed: onClick,
      i18n,
    })
  }

  if (acl && !checkAcl(acl, user, dname)) {
    return null
  }

  if (cornerContent) {
    content = (
      <>
        <span style={{position: 'absolute', fontSize: '0.8em', top: 1, right: 1}}>
          {cornerContent}
        </span>
        {content}
      </>
    )
  }

  if (isString(icon) && Icons[icon]) {
    icon = vicon(icon)
  } else if (isObject(icon) && Icons[icon.name]) {
    let {name, ...icon_props} = icon
    icon = vicon(name, icon_props)
  } else if (isArray(icon)) {
    let [icon1, icon2, icon3] = icon
    let decode = i => (isString(i) ? {name: i, disabled} : {...i, disabled})
    children = (
      <>
        <Icon.Group>
          <VIcon {...decode(icon1)} />
          {icon2 && <VIcon corner="bottom right" {...decode(icon2)} />}
          {icon3 && <VIcon corner="bottom left" {...decode(icon3)} />}
        </Icon.Group>
        {(content || children) && (
          <span>
            {children}
            {content}
          </span>
        )}
      </>
    )
    icon = undefined
    content = undefined // pushed into children
  }
  let kbdshortcut = null
  if (shortcut) {
    kbdshortcut = <KeyboardShortCut name={shortcut} onTrigger={props.onClick} />
  }
  return (
    <>
      <Menu.Item
        className={clsx(
          prominent && styles.prominent,
          className,
          !children && !content && styles.iconOnly
        )}
        content={content}
        icon={icon}
        disabled={disabled}
        active={active}
        {...props}
      >
        {children}
      </Menu.Item>
      {confirmation}
      {kbdshortcut}
    </>
  )
}

const SuiSep = ({style, className}) => (
  <div style={style} className={clsx(styles.separator, className)}></div>
)

const useDebouncedInputValue = ({
  value,
  defaultValue,
  debounce,
  debounceLeading,
  onValueChange,
}) => {
  // track modified text (reset if external input changes)
  // also if debounced onValueChange -> value rountrip
  let [activeValue, setActiveValue] = useResettableState(null, [value])
  let [debounced] = useDebounce(activeValue, parseInt(debounce) || 0, {
    leading: debounceLeading,
  })
  useEffect(() => {
    if (onValueChange && debounced !== null) {
      onValueChange(debounced)
    }
  }, [debounced])

  if (activeValue === null) {
    if (!isNil(value)) {
      activeValue = value
    } else if (defaultValue) {
      activeValue = defaultValue
    }
  }
  return {activeValue, setActiveValue}
}

let XInput = ({
  defaultValue, // init value - control internally
  value, // control input externally
  width,
  maxWidth,
  fluid,
  fitted,
  onValueChange,
  onEnterKey,
  debounce,
  debounceLeading,
  autoFocus,
  selectOnFocus = false, // full select on focus
  icon,
  validator, // regexp for editmask
  onFocus,
  ...props
}) => {
  let {activeValue, setActiveValue} = useDebouncedInputValue({
    value,
    defaultValue,
    debounce,
    debounceLeading,
    onValueChange,
  })
  let outerStyle = fluid ? {flex: 1} : {}

  let style = fluid ? {flex: 1} : {}
  if (width) {
    style = {...style, width: `${width}px`}
  }
  if (maxWidth) {
    style = {...style, maxWidth: `${maxWidth}px`}
  }
  if (fitted) {
    style = {...style, padding: 0, border: '0px'}
    outerStyle = {...outerStyle, paddingRight: '2px', paddingLeft: '2px'}
  }

  let handleChange = (event, data) => {
    if (validator && !new RegExp(validator).test(data.value)) {
      return // don't update state if validation fails
    }
    setActiveValue(data.value)
  }

  let handleFocus = event => {
    // If searchAspect is true, select the input's text
    if (selectOnFocus) {
      event.target.select()
    }
    callIfFunc(onFocus, event)
  }

  return (
    <XItem fitted style={outerStyle}>
      <Input
        icon={icon}
        input={<input style={style} />}
        autoFocus={autoFocus}
        value={activeValue ?? ''}
        onChange={handleChange}
        onKeyDown={event =>
          event.key == 'Enter' ? callIfFunc(onEnterKey, activeValue, event) : null
        }
        fluid={fluid}
        onFocus={handleFocus}
        {...props}
      ></Input>
    </XItem>
  )
}

const FilterInput = ({debounce = 500, placeholder = 'Filtern...', ...props}) => {
  return <XInput icon="filter" placeholder={placeholder} debounce={debounce} {...props} />
}

let XSliderInput = ({
  min = 0,
  max = 10,
  value,
  defaultValue,
  onValueChange,
  width,
  debounce,
  debounceLeading,
  content, // XXX will be dumped
  icon,
  valueLabeled = false,
  defaultOnIconClick = false,
  ...props
}) => {
  let {activeValue, setActiveValue} = useDebouncedInputValue({
    value,
    defaultValue,
    debounce,
    debounceLeading,
    onValueChange,
  })
  let icon_props = {}
  if (defaultOnIconClick && isNumber(defaultValue)) {
    icon_props = {
      onClick: () => setActiveValue(defaultValue),
      style: {cursor: 'pointer'},
    }
  }

  return (
    <Menu.Item {...props}>
      {icon && <VIcon name={icon} {...icon_props} />}
      {valueLabeled && (
        <span
          style={{
            position: 'absolute',
            fontSize: '0.5em',
            bottom: 0,
            left: '40%',
          }}
        >
          {activeValue}
        </span>
      )}
      <input
        name="cheese"
        type="range"
        min={min}
        max={max}
        value={activeValue}
        onChange={event => setActiveValue(parseInt(event.target.value))}
        readOnly={onValueChange ? false : true}
        style={width ? {width} : undefined}
      />
    </Menu.Item>
  )
}

let XPopupItem = ({
  open, // manage openeness externally
  children,
  wide,
  keepOpen = false, // do not close on outside click, openess is managed by Item/click
  closeOnChildClick = false, // close if some div for children has been clicked
  basic = false,
  hideOnScroll,
  flowing,
  padded,
  position,
  positionFixed,
  onOpen,
  onClose,
  onClick,
  popupStyle,
  renderAlways = false, // do not unmount spawned dialog/modals if cosed (render invisible)
  closeOnPopupDescendantClick = true,
  ...props
}) => {
  // if external open is touched, reset internal state
  let [isOpen, setOpen] = useResettableState(false, [open])
  if (isNil(open)) {
    open = isOpen
  }

  let onClickOpen = useCallback(() => {
    setOpen(!open)
    callIfFunc(onOpen)
    callIfFunc(onClick)
  })

  let onPopupClose = useCallback(event => {
    const hasPopupAncestor = el => !!el && !!el.closest('.popup')
    if (hasPopupAncestor(event.target)) {
      // if click-event stems from element that has popup as ancestor -> keep this popup open
      // i.e. clicking within content of a popup that is child of another popup (e.g. in toolbar -> more -> popupItem)
      // note: doesn't check whether this popup is actual trigger-item that opens parent-context of event.target
      if (!closeOnPopupDescendantClick) return
    }
    if (!keepOpen) {
      setOpen(false)
    }
    // probably external open control
    callIfFunc(onClose)
  })

  let handleChildClick = event => {
    if (closeOnChildClick) {
      setOpen(false)
      callIfFunc(onClose)
    }
  }

  return (
    <XPopup
      on="click"
      open={renderAlways || open}
      basic={basic}
      flowing={flowing}
      hideOnScroll={hideOnScroll}
      wide={wide}
      className={clsx(
        padded && styles.paddedPopup,
        styles.popup,
        renderAlways && !open && styles.closed
      )}
      position={position}
      onClose={event => (open ? onPopupClose(event) : undefined)}
      trigger={<XItem {...props} active={open} onClick={onClickOpen} />}
      style={popupStyle}
    >
      <div onClick={handleChildClick}>{children}</div>
    </XPopup>
  )
}

let CopyToClipboardItem = ({text, name, showName = true, ...props}) => {
  let successNotification = useActions(Actions.successNotification)
  let copy = () => {
    navigator.clipboard.writeText(text)
    successNotification({message: `${text.length} Zeichen kopiert.`})
  }
  return (
    <XMenu.Item onClick={copy} title={name ?? 'Copy to Clipboard'} {...props}>
      <VIcon name="copy" />
      {showName ? name ?? 'Copy to Clipboard' : null}
    </XMenu.Item>
  )
}

let DialogItem = ({
  dlg, // dialog content (if children is used for menu item aspect)
  children, // dialog content (will also be used as menu children if dlg is given)
  dlgMode = 'resizable', // or "modal"
  name = 'unnamedDlg', // store resizable props under this name
  dlgIcon = null,
  dlgClose = true,
  dlgHeader = null,
  dlgModalSize = 'small',
  dlgModalActions = null,
  dlgStoreKey = undefined,
  fullscreenButton,
  open, // control externally
  closeDep, // if open is managed internally, close if this dependency change
  onClose, // if open is managed externally, call this func on close
  ...props
}) => {
  let [dialogOpen, setDialogOpen] = useResettableState(false, [closeDep])
  if (!dlg) {
    // use children as dlg content
    dlg = children
  } else {
    // use children for menu item
    props = {...props, children}
  }
  if (!isNil(open)) {
    dialogOpen = open
  }
  let dialog = null
  let close = () => (isFunction(onClose) ? onClose() : setDialogOpen(false))
  let escapeKey = <KeyboardShortCut name="Escape" onTrigger={close} />
  let dialog_node = null
  let {domNode} = useFullscreenContext()
  if (dlgMode == 'modal') {
    dialog_node = (
      <Modal
        className={styles.modal}
        size={dlgModalSize == 'normal' ? undefined : dlgModalSize}
        open={dialogOpen}
        mountNode={domNode || undefined}
        dimmer={() => 'blurring'}
      >
        <div className={styles.header}>
          <Modal.Header as="h2">{dlgHeader}</Modal.Header>
          {dlgClose && (
            <Button icon secondary onClick={() => close()} className={styles.headerButton}>
              <VIcon name="close" />
            </Button>
          )}
        </div>
        <Modal.Content content={dlg} />
        {dlgModalActions && <Modal.Actions>{dlgModalActions}</Modal.Actions>}
        {escapeKey}
      </Modal>
    )
  } else if (dlgMode == 'resizable' && dialogOpen) {
    dialog_node = (
      <>
        {escapeKey}
        <ResizableDialog
          name={name}
          onClose={close}
          icon={dlgIcon}
          header={dlgHeader}
          children={dlg}
          storeKey={dlgStoreKey}
          fullscreenButton={fullscreenButton}
        />
      </>
    )
  }

  return (
    <>
      <XItem onClick={() => setDialogOpen(!dialogOpen)} {...props} active={dialogOpen} />
      {dialog_node}
    </>
  )
}

const LinkItem = ({
  // props of menu item
  children,
  active,
  icon,
  content,
  disabled, // also for <Link/>
  // props for <Link/>
  ...props
}) => {
  /* build a <Link/> and simulate a sui menu item via css classes
   */
  if (isObject(icon)) {
    icon = <VIcon {...icon} />
  } else if (isString(icon)) {
    icon = <VIcon name={icon} />
  } else {
    icon = null
  }
  children = children || (
    <>
      {icon}
      {content}
    </>
  )
  return (
    <Link
      {...props}
      disabled={disabled}
      className={clsx(
        'item',
        active && 'active',
        disabled && 'disabled',
        !content && styles.iconOnly
      )}
    >
      {children}
    </Link>
  )
}

const CallApiItem = ({method, params, onCalled, children, ...props}) => {
  /* combine a XItem with callApi for given method and params
   */
  let callApi = useActions(Actions.callApi)
  let onClick = () => {
    if (method) {
      callApi(method, params || {}, onCalled)
    } else {
      log.warn('no api method given')
    }
  }
  return (
    <XItem onClick={onClick} {...props}>
      {children}
    </XItem>
  )
}

const ButtonGroupItem = ({
  active, // if multi either {xx: true, yy: false, zz: true} or single name if !multi
  onChange, // emit dict {xx: true, yy: false, zz: true} or scalar if !multi
  buttons, //[{name, children, ...props}, ]
  multi = false, // either multi or single select
}) => {
  if (isString(buttons[0])) {
    buttons = buttons.map(b => ({name: b, content: b}))
  }
  let [_active, _setActive] = useState(dict(buttons.map(b => [b.name, false])))
  if (active) {
    if (multi) {
      _active = active
    } else {
      _active = {[active]: true}
    }
  }
  let setActive = (name, value) => {
    let next_active = _active
    if (multi) {
      next_active = {...next_active, [name]: value}
    } else {
      next_active = {[name]: value}
    }
    _setActive(next_active)
    onChange(multi ? next_active : name)
  }
  return buttons.map(({name, children, ...props}) => (
    <XItem
      key={name}
      active={!!_active[name]}
      onClick={() => setActive(name, !_active[name])}
      {...props}
    >
      {children}
    </XItem>
  ))
}

const LogChannelItem = ({channel, ...props}) => {
  let toggleLogStream = useActions('toggleLogStream')
  return (
    <XMenu.Item
      onClick={() => toggleLogStream({channel: 'vls.scheduler'})}
      icon="binoculars"
      {...props}
    />
  )
}
// ---Exports

XMenu.BeginRight = ({...props}) => <XItem fitted position="right" {...props} />
XMenu.Dropdown = ({...props}) => <XDropdown {...props} />

XMenu.Input = XInput
XMenu.Slider = XSliderInput
XMenu.Item = XItem
XMenu.FilterInput = FilterInput
XMenu.PopupItem = XPopupItem
XMenu.PPrintButton = PPrintButton
XMenu.DebugButton = DebugButton // PPrintButton in popup form
XMenu.CopyToClipboardItem = CopyToClipboardItem
XMenu.DialogItem = DialogItem
XMenu.LinkItem = LinkItem
XMenu.ButtonGroupItem = ButtonGroupItem
XMenu.CallApiItem = CallApiItem
XMenu.LogChannelItem = LogChannelItem
// displaynames needed by toolbar.js
XPopupItem.displayName = 'XPopupItem'

XMenu.Label = ({...props}) => <XItem style={{fontWeight: 'bold'}} {...props} />
XMenu.Separator = ({...props}) => <SuiSep {...props} />
XMenu.Toggle = ({value, set, invert = false, ...props}) => (
  <XItem active={invert ? !value : !!value} onClick={() => set(!value)} {...props} />
)
XMenu.ExternalLink = ({href, ...props}) => {
  let apiurl = useSelector(Sel.apiurl)
  return <XItem onClick={() => window.open(apiurl.href(href))} {...props} />
}

XMenu.DownloadLink = ({href, ...props}) => {
  let apiurl = useSelector(Sel.apiurl)
  return (
    <XItem>
      <a download href={apiurl.href(href)}>
        <Icon fitted name="download" />
      </a>
    </XItem>
  )
}

XMenu.SubMenu = ({children, hideOnScroll = true, keepOpen = false, padded = false, ...props}) => {
  /* combines a Popup and a vertical Menu
   */
  let [open, setOpen] = useState(false)

  let onChildClick = orgOnClick => (...args) => {
    if (!keepOpen) {
      setOpen(false)
    }
    return orgOnClick(...args)
  }

  let intercepted = ['onClick', 'set']
  children = applyProps(children, props => {
    if (ldash_size(props) == 0) return props // hack for <Separator/>, what about <Label>?
    let candidates = intercepted.filter(name => props[name])
    if (candidates.length) {
      let name = candidates[0]
      return {
        ...props,
        [name]: onChildClick(props[name]),
      }
    } else {
      // fragile .Dropdown detection (handle onSetValue?)
      let is_ok = !!props.onSetValue || !!props.onValueChange || keepOpen
      if (!is_ok) {
        log.warn(
          'we need to intercept a onClick on child items for closing submenu popup (on %o) (or set keepOpen)',
          keys(props)
        )
      }
    }
  })

  return (
    <XPopupItem
      padded={padded}
      basic
      hideOnScroll={hideOnScroll}
      keepOpen={keepOpen}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      {...props}
    >
      <XMenu padded={padded} vertical size="large">
        {children}
      </XMenu>
    </XPopupItem>
  )
}

XMenu.DatePicker = ({submitOnQuickSelect = false, minDate = '2010', ...props}) => {
  /* complex daterange + granularity picker
   */
  //~ let [opts, setOpts] = useState({granularity: undefined, startDate: minDate, endDate: null})
  return (
    <DatePicker
      submitOnQuickSelect={submitOnQuickSelect}
      minDate={minDate}
      //~ startDate={opts.startDate}
      //~ endDate={opts.endDate}
      //~ granularity={opts.granularity}
      //~ onChange={setOpts}
      {...props}
    />
  )
}

XMenu.DateInput = ({
  onValueChange, // date => {}
  onChange, // event => {}
  value,
  icon,
  emitInvalid = false,
  debounce, // only call onValueChange if some milliseconds have passed
  debounceLeading, // see lodash.debounce + leading
  ...props
}) => {
  /* simple native date picker
  emitInvalid: emit empty/invalid dates during editing (with keyboard)
   */
  let [_value, _setValue] = useState(null)
  let _onChange = useCallback(e => {
    _setValue(e.target.value)
    callIfFunc(onChange, e)
  })

  let [debounced] = useDebounce(_value, parseInt(debounce) || 0, {leading: debounceLeading})
  useEffect(() => {
    if (debounced === null) return // during init
    if (emitInvalid || isValid(parse(debounced, 'yyyy-MM-dd', new Date()))) {
      callIfFunc(onValueChange, debounced)
    }
  }, [debounced])

  if (isString(icon)) {
    icon = {name: icon}
  }
  if (_value !== null) {
    value = _value
  }

  return (
    <XMenu.Item>
      {icon && <Icon {...icon} />}
      <input type="date" value={value} onChange={_onChange} />
    </XMenu.Item>
  )
}

export {XMenu}

export const XDropdown = ({
  iconOnly = false,
  labeled = false, // sets labeled, compact (use with text property)
  children = null,
  attached = false, // eliminates topBorderRadius of Dropdown Menu
  onChange,
  // XXX: rename to onValueChange
  onSetValue, // slimmer than onChange, receives only the selected option value
  onValueChange,
  disabled,
  options, // [{text, value, icon}, ...] or use children
  trigger, // render outer element, can also use a function ({value, text) (instead of a plain react comp)
  minWidth, // reduce size, if selected item is short (will expand size on longer items)
  style,
  ...props
}) => {
  let isOffline = useIsOffline()
  disabled = disabled || isOffline

  let labeledProps = null
  labeled
    ? (labeledProps = {
        labeled: true,
        compact: true,
      })
    : null

  // XXX: remove onSetValue
  if (onValueChange || onSetValue) {
    onChange = useCallback(
      (e, data) => (onValueChange ? onValueChange(data.value) : onSetValue(data.value)),
      [onValueChange, onSetValue]
    )
  }

  if (options && isString(options[0])) {
    // transform simple strings in compatible option datastructure
    options = options.map(v => ({text: v, value: v}))
  }

  // if trigger is a function, evaluate with active values
  if (isFunction(trigger)) {
    // assume controlled component
    let value = props.value
    let text = options?.find(opt => opt.value == value)?.text
    trigger = trigger({value, text})
  }

  let dropdownStyle = style
  if (minWidth) {
    dropdownStyle = {...style, minWidth: `${minWidth}px`}
  }

  return (
    <Dropdown
      item
      onChange={onChange}
      className={clsx(
        styles.dropdown,
        attached && styles.attached,
        iconOnly && styles.iconOnly,
        labeled && clsx(styles.labeled, 'icon')
      )}
      disabled={disabled}
      options={options?.map(({icon, ...opt}) => ({
        ...opt,
        content: icon ? (
          <>
            {<VIcon name={icon} style={{marginRight: '0.5rem'}} />}
            {opt.text}
          </>
        ) : (
          undefined
        ),
      }))}
      trigger={trigger}
      style={dropdownStyle}
      {...props}
      {...labeledProps}
    >
      {children}
    </Dropdown>
  )
}

export const Confirmation = ({open, options, onClose, content}) => {
  let i18n = useSelector(Sel.i18n)
  let runConfirmed = () => {
    options.onConfirm()
    onClose()
  }

  if (isNil(content)) {
    content = options?.content
  }

  let confirmation = React.createElement(buildConfirmation, {
    confirmSpec: content,
    confirmationOpen: open,
    setConfirmationOpen: onClose,
    onConfirmed: runConfirmed,
    i18n,
  })

  return <>{confirmation}</>
}

export const DDMenu = ({icon, text, children, ddsymbol = true, ...props}) => {
  if (!ddsymbol) {
    props.icon = null
  }
  if (icon) {
    props.trigger = (
      <>
        {<Icon name={icon} />}
        {text}
      </>
    )
  } else if (text) {
    props.text = text
  }
  return (
    <Dropdown {...props}>
      <Dropdown.Menu>{children}</Dropdown.Menu>
    </Dropdown>
  )
}

DDMenu.Item = Dropdown.Item

export const ConfirmButton = ({
  // ConfirmationButton
  confirm, // show confirm button, true/false or renderable content or full options
  children,
  ...props
}) => {
  /* A Button with confirmation support
   */
  let i18n = useSelector(Sel.i18n)
  let confirmation = null
  let [confirmationOpen, setConfirmationOpen] = useState(false)

  if (confirm) {
    let {onClick, ...otherProps} = props
    props = {...props, onClick: () => setConfirmationOpen(true)}

    confirmation = React.createElement(buildConfirmation, {
      confirmSpec: confirm,
      confirmationOpen,
      setConfirmationOpen,
      onConfirmed: onClick,
      i18n,
    })
  }
  return (
    <>
      <Button {...props}>{children}</Button>
      {confirmation}
    </>
  )
}

export const ProgressMenuItem = ({value = 20, total = 80, suffix, ...props}) => {
  return (
    <Menu.Item fitted {...props}>
      <div style={{paddingLeft: '1em', paddingRight: '1em'}}>
        <span>{value !== total ? `${value}/${total}` : String(value)}</span>
        {suffix && <span>{suffix}</span>}
      </div>
      {value > 0 && value < total && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'teal',
            width: `${(value / total) * 100}%`,
            height: '60%',
            opacity: 0.2,
            lineHeight: '1.5rem',
          }}
        />
      )}
    </Menu.Item>
  )
}

export const GlobalMenu = ({children, ...props}) => {
  return (
    <AutoPortal target="TabMenuPortal">
      <Menu borderless {...props}>
        {children}
      </Menu>
    </AutoPortal>
  )
}
const usePersistentItemData = ({name, menuProps, initialAsDefault = false}) => {
  let {initialValue, version = 1, ...props} = menuProps
  let [value, setValue] = usePersistentWithQueryOverride(
    {
      name,
      initialValue,
      replace: true,
    },
    version
  )
  if (initialAsDefault) {
    props = {...props, defaultValue: initialValue}
  }
  if (!props.content && !props.icon && !props.children) {
    // default content (is a bad idea for items that spawn childen automaticly)
    props = {...props, content: name}
  }
  return [value, setValue, props]
}

export const usePersistentToggle = (name, menuProps = {}) => {
  /* useToggle with persistence
   */
  if (!isString(name)) {
    if (isObject(name) && !menuProps) {
      menuProps = name
    }
    log.warn('unnamed persitent item')
    name = 'unnamed_toggle'
    menuProps.content = name
  }
  let [value, setValue, props] = usePersistentItemData({name, menuProps})
  value = value || false // initial empty string to false
  return [value, <XMenu.Toggle key={name} value={value} set={setValue} {...props} />]
}

export const useToggle = (name, menuProps = {}) => {
  /* useToggle without persistence
   */
  let {initialValue = false, ...props} = menuProps
  let [value, _setValue] = useState(initialValue)
  let setValue = useCallback(() => _setValue(!value), [value])
  return [value, <XMenu.Toggle key={name} value={value} set={setValue} {...props} />]
}

export const usePersistentButtonGroup = (name, menuProps = {}) => {
  /* */
  let [value, setValue, props] = usePersistentItemData({name, menuProps})
  value = value || menuProps.initialValue // XXX: why is this not default?
  return [value, <XMenu.ButtonGroupItem key={name} active={value} onChange={setValue} {...props} />]
}

export const usePersistentDropdown = (name, menuProps = {}) => {
  /*
  options in menuProps [{text, value}]
   */
  let [value, setValue, props] = usePersistentItemData({name, menuProps})
  return [value, <XMenu.Dropdown value={value} onSetValue={setValue} {...props} />]
}

export const usePersistentSlider = (name, menuProps = {}) => {
  /* options in menuProps [{text, value}]
   */
  let [value, setValue, props] = usePersistentItemData({name, menuProps, initialAsDefault: true})
  return [value, <XMenu.Slider key={name} value={value} onValueChange={setValue} {...props} />]
}

export const usePersistentInput = (name, menuProps = {}) => {
  /* useToggle
  options in menuProps [{text, value}]
   */
  let [value, setValue, props] = usePersistentItemData({name, menuProps})
  // hmm, url param decoding on otid param gives us ints here
  value = String(value)
  return [value, <XInput key={name} value={value} onValueChange={setValue} {...props} />]
}

const buildPersistentItem = ([value, item]) => ({value, item})
export const usePersistentItems = ({toggle, dropdown, slider}) =>
  dict([
    ...mapItems(toggle, (name, props) => [
      name,
      buildPersistentItem(usePersistentToggle(name, props)),
    ]),
    ...mapItems(dropdown, (name, props) => [
      name,
      buildPersistentItem(usePersistentDropdown(name, props)),
    ]),
    ...mapItems(slider, (name, props) => [
      name,
      buildPersistentItem(usePersistentSlider(name, props)),
    ]),
  ])
